import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { configurationIssues } from './lib/supabase';
import { API_URL, apiConfigurationIssues } from './lib/api';
import { AppShell } from './components/AppShell';
import { Loading } from './components/Loading';
import Login from './pages/Login';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Payments = lazy(() => import('./pages/Payments'));
const StaffAdvances = lazy(() => import('./pages/StaffAdvances'));
const Approvals = lazy(() => import('./pages/Approvals'));
const Reports = lazy(() => import('./pages/Reports'));
const GeneralLedger = lazy(() => import('./pages/GeneralLedger'));
const MastersPage = lazy(() => import('./pages/Masters'));
const Administration = lazy(() => import('./pages/Administration'));
const Borrowings = lazy(() => import('./pages/Borrowings'));
const PettyCash = lazy(() => import('./pages/PettyCash'));
const BankReconciliation = lazy(() => import('./pages/BankReconciliation'));
const FixedAssets = lazy(() => import('./pages/FixedAssets'));
const ManualJournals = lazy(() => import('./pages/ManualJournals'));
const OperationsControl = lazy(() => import('./pages/OperationsControl'));
const BulkImport = lazy(() => import('./pages/BulkImport'));

function RouteLoading() {
  return <div className="loading"><Loading label="Loading module…" /></div>;
}

function BackendStartupError({ message, onRetry, onSignOut }: { message: string; onRetry: () => Promise<void>; onSignOut: () => Promise<void> }) {
  return <main className="startup-error-page"><section className="startup-error-card">
    <div className="configuration-error-mark">M</div>
    <span className="eyebrow">MAGNA ACCOUNTING V3</span>
    <h1>Signed in, but the accounting API is unavailable</h1>
    <p>{message}</p>
    <div className="startup-error-meta"><strong>API</strong><code>{API_URL}</code></div>
    <div className="startup-error-actions">
      <button className="button primary" onClick={() => void onRetry()}>Retry connection</button>
      <button className="button secondary" onClick={() => void onSignOut()}>Sign out</button>
    </div>
    <small>Your Supabase data is not changed by this screen. The app will continue as soon as the v3 API is reachable and your profile can be loaded.</small>
  </section></main>;
}

function Protected({ children }: { children: ReactNode }) {
  const { session, loading, startupError, refreshProfile, signOut } = useAuth();
  if (loading) return <div className="full-loading"><Loading label="Starting MAGNA Accounting v3…"/></div>;
  if (!session) return <Navigate to="/login" replace />;
  if (startupError) return <BackendStartupError message={startupError} onRetry={refreshProfile} onSignOut={signOut} />;
  return <AppShell><Suspense fallback={<RouteLoading/>}>{children}</Suspense></AppShell>;
}

function AppRoutes(){
  return <Routes>
    <Route path="/login" element={<Login/>}/>
    <Route path="/*" element={<Protected><Routes>
      <Route index element={<Dashboard/>}/>
      <Route path="expenses" element={<Expenses/>}/>
      <Route path="payments" element={<Payments/>}/>
      <Route path="fixed-assets" element={<FixedAssets/>}/>
      <Route path="staff-advances" element={<StaffAdvances/>}/>
      <Route path="borrowings" element={<Borrowings/>}/>
      <Route path="operations-control" element={<OperationsControl/>}/>
      <Route path="microfinance-savings" element={<Navigate to="/operations-control" replace/>}/>
      <Route path="petty-cash" element={<PettyCash/>}/>
      <Route path="bank-reconciliation" element={<BankReconciliation/>}/>
      <Route path="approvals" element={<Approvals/>}/>
      <Route path="reports" element={<Reports/>}/>
      <Route path="general-ledger" element={<GeneralLedger/>}/>
      <Route path="manual-journals" element={<ManualJournals/>}/>
      <Route path="masters" element={<MastersPage/>}/>
      <Route path="bulk-import" element={<BulkImport/>}/>
      <Route path="administration" element={<Administration/>}/>
      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes></Protected>}/>
  </Routes>;
}

function ConfigurationError({issues}:{issues:string[]}){
  return <main className="configuration-error-page"><section className="configuration-error-card">
    <div className="configuration-error-mark">M</div><h1>MAGNA Accounting v3 configuration required</h1>
    <p>The application was not started because its public connection settings are incomplete or malformed.</p>
    <ul>{issues.map(issue=><li key={issue}>{issue}</li>)}</ul>
    <p className="configuration-help">Run <code>AUTO-DEPLOY-MAGNA-ACCOUNTING-V3.cmd</code> again. Never put the Supabase secret/service-role key in the frontend.</p>
  </section></main>;
}

export default function App(){
  const issues=[...configurationIssues,...apiConfigurationIssues];
  if(issues.length) return <ConfigurationError issues={issues}/>;
  return <BrowserRouter><AuthProvider><AppRoutes/></AuthProvider></BrowserRouter>;
}
